import React, { useEffect, useState } from 'react';
import { PiGearFill } from "react-icons/pi";
import { MdClose } from 'react-icons/md';
import { NumericFormat } from 'react-number-format';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { fetchPortfolios, selectPortfolios, selectStatus } from '../portfolios-slice';
import { selectAccessToken } from '../../authentication/authentication-slice';
import RebalanceItem from '../../../api/models/rebalanceItem';
import PortfolioRebalanceItem from './portfolio-rebalance-item';

/**
 * A collapsible table that shows the asset allocation of the portfolio.
 */
const PortfolioRebalance: React.FC = () => {

  const [configureExpanded, setConfigureExpanded] = useState(true);
  const [allowFractionQty, setAllowFractionQty] = useState(false);
  const [includeNewInvestment, setIncludeNewInvestment] = useState(false);
  const [newInvestmentValue, setNewInvestmentValue] = useState(0);

  // Get the 'dispatch' method from the store
  const dispatch = useAppDispatch();
  const accessToken = useAppSelector(selectAccessToken) ?? '';
  const portfolios = useAppSelector(selectPortfolios);
  const status = useAppSelector(selectStatus);
  const rebalanceItemsAll: RebalanceItem[] = [];
  const rebalanceItemsBuy: RebalanceItem[] = [];
  const rebalanceItemsSell: RebalanceItem[] = [];

  if (portfolios && portfolios[0]) {
    const rebalancePlan = portfolios[0].createRebalancePlan(includeNewInvestment ? newInvestmentValue : 0, allowFractionQty);
    rebalancePlan.rebalanceItems.sort((itemA, itemB) => itemA.position.deviation() > itemB.position.deviation() ? -1 : 1);
    rebalancePlan.rebalanceItems.forEach(rebalanceItem => {
      if (rebalanceItem.quantity > 0) {
        rebalanceItemsAll.push(rebalanceItem);
        rebalanceItemsBuy.push(rebalanceItem);
      } else if (rebalanceItem.quantity < 0) {
        rebalanceItemsAll.push(rebalanceItem);
        rebalanceItemsSell.push(rebalanceItem);
      }
    });
  }

  const valueToBuy = rebalanceItemsBuy.filter(item => !item.done).map(item => item.notional()).reduce((acc, cur) => acc + cur, 0);
  const valueToSell = rebalanceItemsSell.filter(item => !item.done).map(item => item.notional()).reduce((acc, cur) => acc + cur, 0);
  const valueDoneBuy = rebalanceItemsBuy.filter(item => item.done).map(item => item.notional()).reduce((acc, cur) => acc + cur, 0);
  const valueDoneSell = rebalanceItemsSell.filter(item => item.done).map(item => item.notional()).reduce((acc, cur) => acc + cur, 0);

  // React useEffect: applies after rendering. Once, as no dependencies are included.
  useEffect(() => {
    if (status === 'idle') {
      const execute = async () => {
        dispatch(fetchPortfolios(accessToken));
      }
      execute();
    }
  });

  return (
    portfolios && portfolios.length > 0 &&
    <div className='flex flex-col w-full h-full items-center justify-start lg:p-10 overflow-y-auto text-neutral-200 dark:text-neutral-300'>
      <div className='grow w-full lg:w-3/5 lg:rounded-2xl overflow-y-auto bg-abstract dark:bg-abstract bg-cover lg:border-2 border-blue-300/40 shadow-2xl shadow-slate-900/80'>
        <table className='w-full text-xs lg:text-base'>
          <thead className='sticky top-0 bg-slate-900 border-y border-blue-300/30 uppercase'>
            <tr>
              <th colSpan={5} className='p-0'>
                <td className='flex w-full p-0'>
                  <div className={`${configureExpanded ? 'flex flex-col h-fit py-5' : 'h-0 overflow-hidden p-0'} w-full px-5 font-normal text-xs border-b border-blue-300/40 transition-all duration-300`}>
                    <div className='flex h-fit w-full items-center'>
                      <div onClick={(e) => setAllowFractionQty(!allowFractionQty)} className='cursor-pointer flex items-center'>
                        <input checked={allowFractionQty} type='checkbox' className='w-4 h-4' />
                        <label className='cursor-pointer ml-1'>Habilitar Fracionário</label>
                      </div>
                      <div className='cursor-pointer flex grow justify-end'>
                        <MdClose onClick={() => setConfigureExpanded(!configureExpanded)} size={20} />
                      </div>
                    </div>
                    <div className='flex h-fit w-full items-center pt-5'>
                      <div onClick={(e) => setIncludeNewInvestment(!includeNewInvestment)} className='cursor-pointer flex items-center'>
                        <input checked={includeNewInvestment} type='checkbox' className='w-4 h-4' />
                        <label className='cursor-pointer ml-1'>Incluir novo aporte</label>
                      </div>
                    </div>
                    <div className={`${includeNewInvestment ? 'h-10' : 'h-0 overflow-hidden'} flex flex-col w-full items-start pl-5 pt-2 transition-all duration-300`}>
                      <NumericFormat
                        prefix='R$ '
                        onValueChange={(v) => { setNewInvestmentValue(v.floatValue ?? 0) }}
                        value={newInvestmentValue}
                        min={0}
                        step={1}
                        decimalSeparator=','
                        thousandSeparator='.'
                        className='text-slate-900 text-sm font-semibold rounded-lg ml-1 px-2 py-1 w-32 text-left' />
                    </div>
                  </div>
                </td>
              </th>
            </tr>
            <tr>
              <th colSpan={3} className='items-center text-end px-3 py-3'>
                <div className={`${configureExpanded ? 'hidden' : 'flex'} items-center`}>
                  <PiGearFill size={25} onClick={() => setConfigureExpanded(!configureExpanded)} className='cursor-pointer' />
                  <label className='grow text-end'>Quantidade</label>
                </div>
              </th>
              <th className='items-center text-end px-3 py-3'>Ativo</th>
              <th className='items-center text-center py-3 pr-2'>Feito</th>
            </tr>
          </thead>
          <tbody>
            {
              rebalanceItemsAll.map(rebalanceItem => <PortfolioRebalanceItem rebalanceItem={rebalanceItem} newInvestmentTotal={includeNewInvestment ? newInvestmentValue : 0} />)
            }
          </tbody>
        </table>
        <div className='flex sticky bottom-0 items-center justify-center w-full border-t lg:border-t-0 bg-slate-900 border-blue-300/40 py-3 px-5 lg:px-10 text-xs lg:text-base'>
          <div className='flex flex-row w-full items-start'>
            <div className='flex flex-col text-start'>
              <label className='underline underline-offset-2 pb-1'>A COMPRAR</label>
              <label>{'R$ ' + valueToBuy.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}</label>
            </div>
            <div className='flex flex-col grow text-end'>
              <label className='underline underline-offset-2 pb-1'>A VENDER</label>
              <label>{'R$ ' + valueToSell.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}</label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PortfolioRebalance;